/*
 * The rules behind a page's chrome, kept apart from its components so they can be checked without
 * a browser: where each sticky layer sits, where the compact header's back chevron goes, what its
 * bar draws at its end, and what the browser tab calls the page.
 */

/**
 * The chrome that sticks to the top of the page scroll: the PageHeader, the row of its tabs under
 * its bar on the compact layout, a PageToolbar under them, and a settings page's AnchorNav when it
 * is a row of chips under the header on the compact layout. On the desk a header's tabs sit in its
 * bar and the AnchorNav in its own pane, so `tabs` and `anchors` are only ever mounted on `compact`.
 */
export type ChromeLayer = "header" | "tabs" | "toolbar" | "anchors"

/** How many of each layer are mounted on the page right now. */
export type MountedChrome = Record<ChromeLayer, number>

/**
 * The sticky ladder's rungs for what is mounted, as whole class names so Tailwind finds each:
 * `underHeader`, where the PageToolbar and a split page's panes stick (under the 48px bar, and
 * under its 40px tab row too on `compact`); `underChrome`, where an in-list header (a table's head,
 * a GroupHeader, a ticket's rail) sticks, under the toolbar as well when there is one, or at the
 * top of a scroller of its own (`OwnScroller`); and
 * `belowHeader`, the height a `master` list has under the header.
 *
 * `stuck` is how tall everything that sticks stands together, in px. `sectionMargin` is how far
 * below the top a section scrolled to its anchor comes to rest: 16px under all of it. `readingBand`
 * is the IntersectionObserver margin of the band an AnchorNav reads its section in: from 8px under
 * everything that sticks to 45% of the way down.
 */
export type Ladder = {
  underHeader: "top-0" | "top-12" | "top-22"
  underChrome: "top-0" | "top-12" | "top-22" | "top-32"
  belowHeader: "h-below-header" | "h-below-header-tabs"
  stuck: number
  sectionMargin: (typeof sectionMargins)[keyof typeof sectionMargins]
  readingBand: string
}

/** Each layer's height in px: the bar, the compact tab row, the toolbar, the compact chip bar. */
const heights: MountedChrome = { header: 48, tabs: 40, toolbar: 40, anchors: 48 }

/** A section's scroll margin for each height of stuck chrome it can rest under, 16px clear. */
const sectionMargins = {
  48: "scroll-mt-16",
  88: "scroll-mt-26",
  96: "scroll-mt-28",
  128: "scroll-mt-36",
  136: "scroll-mt-38",
  176: "scroll-mt-48",
} as const

/**
 * How tall the chrome stuck to the top of the page is, in px: the header, which every page has
 * (counted before it has registered too), and every other layer mounted.
 */
export function stuckHeightOf(mounted: MountedChrome) {
  return (Object.keys(heights) as ChromeLayer[]).reduce(
    (height, layer) => height + (layer === "header" || mounted[layer] > 0 ? heights[layer] : 0),
    0,
  )
}

/** The ladder for what is mounted; with nothing yet registered, the header's, which every page has. */
export function ladderOf(mounted: MountedChrome): Ladder {
  const tabs = mounted.tabs > 0
  const toolbar = mounted.toolbar > 0
  const stuck = stuckHeightOf(mounted)
  return {
    underHeader: tabs ? "top-22" : "top-12",
    underChrome: tabs && toolbar ? "top-32" : tabs || toolbar ? "top-22" : "top-12",
    belowHeader: tabs ? "h-below-header-tabs" : "h-below-header",
    stuck,
    sectionMargin: sectionMargins[stuck as keyof typeof sectionMargins],
    readingBand: `-${stuck + 8}px 0px -55% 0px`,
  }
}

/**
 * Where the compact PageHeader's back chevron goes: the page's own `back`, when it was opened from
 * a view its crumbs don't name (a ticket from My tickets), or else the nearest crumb above the page
 * that links somewhere, rather than `history.back()`, so a page opened straight from a Slack link
 * still goes up to its parent rather than out of Beacon. A tab's own page (Home, My tickets,
 * Projects) has no back, since the tab bar is how a Member leaves it; nor does a page with no
 * linked crumb above it and no `back`.
 */
export function backOf(
  crumbs: readonly BackCrumb[],
  { tabRoot, back }: { tabRoot: boolean; back?: PageBack | undefined },
): (BackCrumb & { href: string }) | PageBack | undefined {
  if (tabRoot) return undefined
  return (
    back ??
    crumbs
      .slice(0, -1)
      .findLast((crumb): crumb is BackCrumb & { href: string } => crumb.href !== undefined)
  )
}

/**
 * Where a page leads back to on `compact` in place of its crumbs: the view it was opened from, by
 * its name and address. `returns` says the history entry before the page's is that view, so the
 * chevron goes Back to it, as the system's Back does, and the view keeps the place it was left at;
 * without it the chevron opens the view anew.
 */
export type PageBack = { label: string; href: string; returns: boolean }

/** A click on a link, as far as deciding whether it follows the link in place. */
export type Click = {
  button: number
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
  defaultPrevented: boolean
}

/**
 * Whether a click on a link is the plain one that follows it in place: a primary click with no
 * modifier, which nothing else has already taken (a drag's last). A modifier or a middle click is
 * the browser's, to open the link elsewhere.
 */
export const opensInPlace = (click: Click) =>
  click.button === 0 &&
  !click.defaultPrevented &&
  !click.metaKey &&
  !click.ctrlKey &&
  !click.shiftKey &&
  !click.altKey

/** What of a crumb the back chevron reads: its name, where it links, and the state it carries. */
type BackCrumb = { label: string; href?: string; state?: unknown }

/**
 * What the browser tab calls a page: its crumbs, most specific first, then Beacon — "Members ·
 * Beacon", "Settings · Operations · Projects · Beacon". A page with a better name for itself than
 * its crumbs, such as a ticket's key and title, passes it as `documentTitle`, and Beacon follows.
 */
export function documentTitleOf(crumbs: readonly { label: string }[], documentTitle?: string) {
  const specific = documentTitle ? [documentTitle] : crumbs.map(({ label }) => label).reverse()
  return [...specific, "Beacon"].join(" · ")
}

/** An entry the compact ⋯ would hold that asks first, opens a submenu, or rules off the rest. */
type Asking = { kind: string }

/**
 * What the compact PageHeader draws at its bar's end: its one icon action, then a ⋯ of `entries`
 * (the secondary actions the ⋯ holds, then `more`). The icon is the page's `primary`. A page with
 * none whose ⋯ would hold one plain entry (no confirmation, no submenu) draws that entry as the
 * icon instead, rather than a ⋯ opening on one row; one that asks first stays in its ⋯.
 */
export function compactEndOf<Primary extends object, Entry extends object>(
  primary: Primary | undefined,
  entries: readonly Entry[],
): { icon: Primary | Exclude<Entry, Asking> | undefined; menu: readonly Entry[] } {
  if (primary !== undefined) return { icon: primary, menu: entries }
  const [only, ...rest] = entries
  if (only !== undefined && rest.length === 0 && plain(only)) return { icon: only, menu: [] }
  return { icon: undefined, menu: entries }
}

const plain = <Entry extends object>(entry: Entry): entry is Exclude<Entry, Asking> =>
  !("kind" in entry)
